import type { AnalysisResult, MarketData, OptionData } from '../types'

export interface TradeSignal {
    type: 'LONG_CALL' | 'LONG_PUT' | 'NO_SIGNAL'
    reason: string
    entryPrice: number
    targetPrice: number
    stopLoss: number
    riskRewardRatio: number
    confidence: 'HIGH' | 'MEDIUM' | 'LOW'
    timestamp: number
    marketCondition: 'BULLISH' | 'BEARISH' | 'RANGE_BOUND'
    strike: number // The option strike to trade
    optionLtp: number // Current price of that option
}

export class TradeEngine {
    /**
     * Calculate Buying Signals (Momentum/Reversal) based on Oracle Rules
     * Rule:
     * - Resistance = Lowest Strike of Top 3 Call OI/Vol
     * - Support = Highest Strike of Top 3 Put OI/Vol
     * - Shift = (R + S) / 2
     */
    static analyze(marketData: MarketData, analysis: AnalysisResult): TradeSignal[] {
        if (!marketData || !marketData.options || marketData.options.length === 0) return []

        const signals: TradeSignal[] = []
        const spot = marketData.spotPrice

        const R = analysis.resistanceStrike
        const S = analysis.supportStrike
        const Shift = (R + S) / 2

        let atm = analysis.atmStrike

        // Fallback ATM calculation if not provided in analysis
        if (!atm && marketData.options.length > 0) {
            const sortedByDistance = [...marketData.options].sort(
                (a, b) => Math.abs(a.strike - spot) - Math.abs(b.strike - spot),
            )
            if (sortedByDistance.length > 0) {
                atm = sortedByDistance[0]!.strike
            }
        }

        if (!atm) return []

        // 2. Determine Zone
        const inBullZone = spot > Shift
        const inBearZone = spot < Shift

        // 3. LONG CALL Opportunity (Buying CE)
        // Strategy A: Bullish above Shift
        if (inBullZone) {
            // Check if we have room to R
            const distToR = R - spot
            // If distance is decent (> 0.2%), good trade
            if (distToR > spot * 0.002) {
                // Find slightly ITM or ATM Call
                const targetStrike = atm
                const opt = TradeEngine.findOption(marketData.options, targetStrike)

                if (opt) {
                    const entry = TradeEngine.getPrice(opt, 'CE')
                    // Project Option Targets based on Delta (approx 0.5 for ATM)
                    const projectedGain = (R - spot) * 0.55
                    const targetOpt = entry + projectedGain
                    // SL: Close below Shift
                    const slDist = (spot - Shift) * 0.5
                    const slOpt = Math.max(0.1, entry - slDist)

                    signals.push({
                        type: 'LONG_CALL',
                        reason: `Bullish momentum > Shift (${Shift}). Target Resistance (${R}).`,
                        entryPrice: parseFloat(entry.toFixed(2)),
                        targetPrice: parseFloat(targetOpt.toFixed(2)),
                        stopLoss: parseFloat(slOpt.toFixed(2)),
                        riskRewardRatio: parseFloat((projectedGain / slDist).toFixed(2)),
                        confidence: 'HIGH',
                        timestamp: Date.now(),
                        marketCondition: 'BULLISH',
                        strike: targetStrike,
                        optionLtp: entry,
                    })
                }
            }
        }

        // Strategy B: Reversal from Support
        if (Math.abs(spot - S) < spot * 0.003 && spot > S) {
            const opt = TradeEngine.findOption(marketData.options, S) // ITM Call at Support
            if (opt) {
                const entry = TradeEngine.getPrice(opt, 'CE')
                const targetSpot = Shift
                const gain = (targetSpot - spot) * 0.6 // Higher delta for ITM
                const slSpot = S - spot * 0.0015
                const loss = (spot - slSpot) * 0.6

                signals.push({
                    type: 'LONG_CALL',
                    reason: `Support Bounce at ${S}. Target Shift (${Shift}).`,
                    entryPrice: parseFloat(entry.toFixed(2)),
                    targetPrice: parseFloat((entry + gain).toFixed(2)),
                    stopLoss: parseFloat((entry - loss).toFixed(2)),
                    riskRewardRatio: parseFloat((gain / loss).toFixed(2)),
                    confidence: 'MEDIUM', // Reversals are riskier than trends
                    timestamp: Date.now(),
                    marketCondition: 'RANGE_BOUND',
                    strike: S,
                    optionLtp: entry,
                })
            }
        }

        // 4. LONG PUT Opportunity (Buying PE)
        // Strategy A: Bearish below Shift
        if (inBearZone) {
            const distToS = spot - S
            if (distToS > spot * 0.002) {
                const targetStrike = atm
                const opt = TradeEngine.findOption(marketData.options, targetStrike)

                if (opt) {
                    const entry = TradeEngine.getPrice(opt, 'PE')
                    // Target Spot = S
                    const projectedGain = (spot - S) * 0.55
                    const targetOpt = entry + projectedGain
                    const slDist = (Shift - spot) * 0.5
                    const slOpt = Math.max(0.1, entry - slDist)

                    signals.push({
                        type: 'LONG_PUT',
                        reason: `Bearish momentum < Shift (${Shift}). Target Support (${S}).`,
                        entryPrice: parseFloat(entry.toFixed(2)),
                        targetPrice: parseFloat(targetOpt.toFixed(2)),
                        stopLoss: parseFloat(slOpt.toFixed(2)),
                        riskRewardRatio: parseFloat((projectedGain / slDist).toFixed(2)),
                        confidence: 'HIGH',
                        timestamp: Date.now(),
                        marketCondition: 'BEARISH',
                        strike: targetStrike,
                        optionLtp: entry,
                    })
                }
            }
        }

        // Strategy B: Rejection from Resistance
        if (Math.abs(spot - R) < spot * 0.003 && spot < R) {
            const opt = TradeEngine.findOption(marketData.options, R) // ITM Put at Resistance
            if (opt) {
                const entry = TradeEngine.getPrice(opt, 'PE')
                const targetSpot = Shift
                const gain = (spot - targetSpot) * 0.6
                const slSpot = R + spot * 0.0015
                const loss = (slSpot - spot) * 0.6

                signals.push({
                    type: 'LONG_PUT',
                    reason: `Resistance Rejection at ${R}. Target Shift (${Shift}).`,
                    entryPrice: parseFloat(entry.toFixed(2)),
                    targetPrice: parseFloat((entry + gain).toFixed(2)),
                    stopLoss: parseFloat((entry - loss).toFixed(2)),
                    riskRewardRatio: parseFloat((gain / loss).toFixed(2)),
                    confidence: 'MEDIUM',
                    timestamp: Date.now(),
                    marketCondition: 'RANGE_BOUND',
                    strike: R,
                    optionLtp: entry,
                })
            }
        }

        return signals
    }

    private static findOption(options: OptionData[], strike: number): OptionData | undefined {
        return options.find((o) => o.strike === strike)
    }

    private static getPrice(opt: OptionData, type: 'CE' | 'PE'): number {
        return type === 'CE' ? opt.callPrice || 0 : opt.putPrice || 0
    }
}
